import React, { useState, useEffect, useRef } from 'react';
import { BookUser, RefreshCw, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useApp } from '@/context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SentSyncWidget({ accountId, autoStart = false }) {
  const [status, setStatus] = useState({ status: 'idle', synced: 0, total: 0 });
  const pollRef = useRef(null);
  const userTriggered = useRef(false);
  const { notifySyncDone } = useApp();

  const pollStatus = async () => {
    try {
      const res = await axios.get(`${API}/accounts/${accountId}/sent-sync-status`);
      setStatus(res.data);
      if (res.data.status === 'done' || res.data.status === 'error') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        if (userTriggered.current) {
          if (res.data.status === 'done') {
            toast.success(res.data.message || 'Rubrica storica aggiornata!');
            notifySyncDone();
          }
          if (res.data.status === 'error') toast.error(`Sync fallito: ${res.data.message}`);
        }
      }
    } catch {}
  };

  const startSync = async () => {
    try {
      userTriggered.current = true;
      await axios.post(`${API}/accounts/${accountId}/sync-sent`);
      setStatus({ status: 'queued', synced: 0, total: 0 });
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(pollStatus, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore avvio sync');
    }
  };

  useEffect(() => {
    pollStatus();
    if (autoStart) setTimeout(startSync, 500);
    return () => clearInterval(pollRef.current);
  }, [accountId]); // eslint-disable-line

  useEffect(() => {
    if (status.status === 'running' || status.status === 'queued') {
      if (!pollRef.current) pollRef.current = setInterval(pollStatus, 1500);
    }
  }, [status.status]); // eslint-disable-line

  const pct = status.total > 0 ? Math.round((status.synced / status.total) * 100) : 0;
  const isActive = status.status === 'running' || status.status === 'queued';

  return (
    <div className="mt-3 rounded-xl p-3 space-y-2" style={{ background: '#F5F0EA', border: '1px solid #E8E0D4' }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookUser size={13} style={{ color: '#2DAA7E' }} />
          <span className="text-xs font-medium text-gray-600">Rubrica storica</span>
        </div>
        {!isActive && (
          <button
            data-testid={`sync-sent-btn-${accountId}`}
            onClick={startSync}
            className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-lg border transition-all hover:bg-white"
            style={{ borderColor: '#2DAA7E', color: '#2DAA7E' }}
          >
            <RefreshCw size={11} />
            {status.status === 'done' ? 'Risincronizza' : 'Sincronizza'}
          </button>
        )}
      </div>

      {status.status === 'idle' && (
        <p className="text-xs text-gray-400">Sincronizza gli Inviati storici per popolare la rubrica con tutti i contatti.</p>
      )}

      {isActive && (
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span className="flex items-center gap-1">
              <Loader2 size={10} className="animate-spin" />
              {status.status === 'queued' ? 'In coda...' : `Analisi email ${status.synced}${status.total > 0 ? ` / ${status.total}` : ''}...`}
            </span>
            {status.total > 0 && <span>{pct}%</span>}
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#D9D2C8' }}>
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${status.total > 0 ? pct : 30}%`, background: '#2DAA7E',
                       animation: status.total === 0 ? 'pulse 1.5s infinite' : 'none' }}
            />
          </div>
        </div>
      )}

      {status.status === 'done' && (
        <p className="text-xs flex items-center gap-1.5" style={{ color: '#2DAA7E' }}>
          <CheckCircle size={11} /> {status.synced} email inviate analizzate — rubrica aggiornata
        </p>
      )}

      {status.status === 'error' && (
        <p className="text-xs flex items-center gap-1.5 text-red-500">
          <XCircle size={11} /> Errore: {status.message}
        </p>
      )}
    </div>
  );
}
